import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ThemeProvider } from './hooks/useTheme';
import Layout from './components/layout/Layout';
import AdminLayout from './components/layout/AdminLayout';
import LoginPage          from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage  from './pages/ResetPasswordPage';
import MastersPage        from './pages/admin/MastersPage';
import DashboardPage from './pages/DashboardPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import TasksPage from './pages/TasksPage';
import TaskDetailPage from './pages/TaskDetailPage';
import BugsPage from './pages/BugsPage';
import BugDetailPage from './pages/BugDetailPage';
import ChangeRequestsPage from './pages/ChangeRequestsPage';
import CRDetailPage from './pages/CRDetailPage';
import TrainingPage from './pages/TrainingPage';
import TrainingDetailPage from './pages/TrainingDetailPage';
import ActivitiesPage from './pages/ActivitiesPage';
import ActivityDetailPage from './pages/ActivityDetailPage';
import ReportsPage from './pages/ReportsPage';
import UsersPage from './pages/UsersPage';
import ChatPage from './pages/ChatPage';
import WorkplanPage from './pages/WorkplanPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminRolesPage from './pages/admin/AdminRolesPage';
import AdminAuditLogsPage from './pages/admin/AdminAuditLogsPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminDataPage from './pages/admin/AdminDataPage';
import AdminAnalyticsPage from './pages/admin/AdminAnalyticsPage';
import AdminProjectTypesPage from './pages/admin/AdminProjectTypesPage';
import AdminPageAccessPage from './pages/admin/AdminPageAccessPage';
import AdminRoleManagementPage from './pages/admin/AdminRoleManagementPage';
import AdminMetadataPage from './pages/admin/AdminMetadataPage';
import NotAuthorizedPage from './pages/NotAuthorizedPage';
import ProfilePage       from './pages/ProfilePage';
import DelegationPage    from './pages/DelegationPage';
import NotificationsPage from './pages/NotificationsPage';
import KanbanPage        from './pages/KanbanPage';
import DevHubPage        from './pages/DevHubPage';
import ClientsPage       from './pages/ClientsPage';
import ClientDetailPage  from './pages/ClientDetailPage';
import { usePageAccess } from './hooks/usePermissions';
import MilestonesPage from './pages/MilestonesPage';
import RisksPage from './pages/RisksPage';
import './index.css';
import MilestoneTimelinePage from './pages/MilestoneTimelinePage';
import TodoPage              from './pages/TodoPage';
import PlatformsPage         from './pages/PlatformsPage';
import AdminPlatformsPage    from './pages/admin/AdminPlatformsPage';
import { PagePermsProvider } from './PagePermsContext';
import { ToastProvider } from './components/Toast';
import { UnsavedGuardProvider } from './components/UnsavedChanges';
import ErrorBoundary from './components/ErrorBoundary';
import SystemSupportPage       from './pages/SystemSupportPage';
import AdminRecycleBinPage     from './pages/admin/AdminRecycleBinPage';
import AdminEmailLogPage       from './pages/admin/AdminEmailLogPage';
import SystemSupportDetailPage from './pages/SystemSupportDetailPage';
import KnowledgePage          from './pages/KnowledgePage';
import { isAdminRole } from './utils/rolesConfig';

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
    <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
  </div>
);
const PrivateRoute = ({ children }) => { const { user, loading } = useAuth(); if (loading) return <Spinner />; return user ? children : <Navigate to="/login" />; };
const PublicRoute  = ({ children }) => { const { user, loading } = useAuth(); if (loading) return <Spinner />; return !user ? children : <Navigate to="/" />; };
const AdminRoute   = ({ children }) => { const { user, loading } = useAuth(); if (loading) return <Spinner />; if (!user) return <Navigate to="/login" />; if (!isAdminRole(user.role)) return <Navigate to="/" />; return children; };


// Checks whether the current user's role can access the current route.
// Must be used INSIDE a route that already has useAuth available.
function PageGuard({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const { allowedPaths, loading } = usePageAccess(user?.id);

  if (loading || !allowedPaths) return (
    <div className="flex justify-center items-center h-screen">
      <div className="w-7 h-7 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/>
    </div>
  );

  // Check if this path (or a parent path) is allowed
  const currentPath = location.pathname;
  const pathAllowed = allowedPaths.has(currentPath) ||
    [...allowedPaths].some(p => p !== '/' && currentPath.startsWith(p));

  if (!pathAllowed) return <NotAuthorizedPage />;
  return children;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        {/* Inside AuthProvider, outside Router: every route and every provider
            below can raise a toast, and the toast layer itself never unmounts
            on navigation. */}
        <ToastProvider>
        {/* Above Router: the sidebar asks it before navigating. */}
        <UnsavedGuardProvider>
        <Router>
          {/* Inside Router: it reads the current path to resolve page permissions. */}
          <PagePermsProvider>
          {/* Inside the layout providers, so a crash in one page keeps the
              sidebar and header alive and you can navigate away from it. */}
          <ErrorBoundary name="app">
          <Routes>
            <Route path="/login"           element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/forgot-password"  element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
            <Route path="/reset-password"   element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
            <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
              <Route index                         element={<DashboardPage />} />
              <Route path="projects"               element={<PageGuard><ProjectsPage /></PageGuard>} />
              <Route path="projects/:id"           element={<PageGuard><ProjectDetailPage /></PageGuard>} />
              <Route path="tasks"                  element={<PageGuard><TasksPage /></PageGuard>} />
              <Route path="tasks/:id"              element={<PageGuard><TaskDetailPage /></PageGuard>} />
              <Route path="activities"             element={<PageGuard><ActivitiesPage /></PageGuard>} />
              <Route path="activities/:id"         element={<PageGuard><ActivityDetailPage /></PageGuard>} />
              <Route path="bugs"                   element={<PageGuard><BugsPage /></PageGuard>} />
              <Route path="bugs/:id"               element={<PageGuard><BugDetailPage /></PageGuard>} />
              <Route path="change-requests"        element={<PageGuard><ChangeRequestsPage /></PageGuard>} />
              <Route path="change-requests/:id"    element={<PageGuard><CRDetailPage /></PageGuard>} />
              <Route path="training"               element={<PageGuard><TrainingPage /></PageGuard>} />
              <Route path="training/:id"            element={<PageGuard><TrainingDetailPage /></PageGuard>} />
              <Route path="knowledge"              element={<PageGuard><KnowledgePage /></PageGuard>} />
              <Route path="workplan"               element={<PageGuard><WorkplanPage /></PageGuard>} />
              <Route path="chat"                   element={<PageGuard><ChatPage /></PageGuard>} />
              <Route path="reports"                element={<PageGuard><ReportsPage /></PageGuard>} />
              <Route path="users"                  element={<PageGuard><UsersPage /></PageGuard>} />
              <Route path="milestones"             element={<PageGuard><MilestonesPage /></PageGuard>} />
              <Route path="risks"                  element={<PageGuard><RisksPage /></PageGuard>} />
              <Route path="todo"                 element={<PageGuard><TodoPage /></PageGuard>} />
              <Route path="kanban"               element={<PageGuard><KanbanPage /></PageGuard>} />
              <Route path="platforms"            element={<PageGuard><PlatformsPage /></PageGuard>} />
              <Route path="system-support"       element={<PageGuard><SystemSupportPage /></PageGuard>} />
              <Route path="system-support/:id"   element={<PageGuard><SystemSupportDetailPage /></PageGuard>} />
              <Route path="dev-hub"              element={<PageGuard><DevHubPage /></PageGuard>} />
              <Route path="clients"              element={<PageGuard><ClientsPage /></PageGuard>} />
              <Route path="clients/:id"          element={<PageGuard><ClientDetailPage /></PageGuard>} />
              <Route path="notifications"         element={<NotificationsPage />} />
              <Route path="profile"               element={<ProfilePage />} />
              <Route path="delegation"             element={<DelegationPage />} />
              <Route path="milestone-timeline"      element={<PageGuard><MilestoneTimelinePage /></PageGuard>} />
            </Route>
            <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
              <Route index               element={<AdminDashboardPage />} />
              <Route path="users"        element={<AdminUsersPage />} />
              <Route path="roles"        element={<AdminRolesPage />} />
              <Route path="audit-logs"   element={<AdminAuditLogsPage />} />
              <Route path="settings"     element={<AdminSettingsPage />} />
              <Route path="data"         element={<AdminDataPage />} />
              <Route path="recycle-bin"  element={<AdminRecycleBinPage />} />
              <Route path="email-log"    element={<AdminEmailLogPage />} />
              <Route path="analytics"    element={<AdminAnalyticsPage />} />
              <Route path="masters"      element={<MastersPage />} />
              <Route path="project-types" element={<AdminProjectTypesPage />} />
              <Route path="page-access"    element={<AdminPageAccessPage />} />
              <Route path="role-management" element={<AdminRoleManagementPage />} />
              <Route path="metadata"        element={<AdminMetadataPage />} />
              <Route path="platforms"       element={<AdminPlatformsPage />} />
            </Route>
          <Route path="/not-authorized" element={<NotAuthorizedPage />} />
          {/* Anything with no route above lands here and is bounced to the
              dashboard. A nav entry whose route is missing therefore looks
              like a permission problem and is not one — check this file first. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
          </ErrorBoundary>
        </PagePermsProvider>
        </Router>
        </UnsavedGuardProvider>
        </ToastProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
